import { difference, union } from "lodash";
import {
  type Identifier,
  RecordContextProvider,
  useListContext,
  useNotify,
  useUpdate,
  useGetList,
} from "ra-core";
import { useCallback, useRef, type MouseEvent } from "react";
import { Link } from "react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar } from "./Avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact, Company } from "../types";

export const ContactGridContent = () => {
  const {
    data: contacts,
    error,
    isPending,
    onToggleItem,
    onSelect,
    selectedIds = [],
  } = useListContext<Contact>();
  const lastSelected = useRef<Identifier | null>(null);

  const { connectionPipelineStages } = useConfigurationContext();
  const [update] = useUpdate<Contact>("contacts");
  const notify = useNotify();

  // Fetch companies for the inline selector
  const { data: companies = [] } = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "name", order: "ASC" },
  });

  const handleToggleItem = useCallback(
    (id: Identifier, event: MouseEvent) => {
      if (!contacts) return;

      const ids = contacts.map((contact) => contact.id);
      const lastSelectedIndex = lastSelected.current
        ? ids.indexOf(lastSelected.current)
        : -1;

      if (event.shiftKey && lastSelectedIndex !== -1) {
        const index = ids.indexOf(id);
        const idsBetweenSelections = ids.slice(
          Math.min(lastSelectedIndex, index),
          Math.max(lastSelectedIndex, index) + 1,
        );

        const isClickedItemSelected = selectedIds.includes(id);
        const newSelectedIds = isClickedItemSelected
          ? difference(selectedIds, idsBetweenSelections)
          : union(selectedIds, idsBetweenSelections);

        onSelect?.(newSelectedIds);
      } else {
        onToggleItem(id);
      }

      lastSelected.current = id;
    },
    [contacts, selectedIds, onSelect, onToggleItem],
  );

  const handleFieldChange = useCallback(
    (contact: Contact, field: keyof Contact, value: any) => {
      if ((contact as any)[field] === value) return;

      update(
        "contacts",
        {
          id: contact.id,
          data: { [field]: value },
          previousData: contact,
        },
        {
          mutationMode: "optimistic",
          onError: (err: any) => {
            notify(
              typeof err === "string"
                ? err
                : err?.message || "Failed to update contact",
              { type: "error" },
            );
          },
        },
      );
    },
    [update, notify],
  );

  if (isPending) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error || !contacts) {
    return null;
  }

  const selectableIds = contacts.map((c) => c.id);
  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.includes(id));

  const handleToggleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelect?.(union(selectedIds, selectableIds));
    } else {
      onSelect?.(difference(selectedIds, selectableIds));
    }
  };

  const getPrimaryEmail = (contact: Contact) => {
    if (
      !contact.email_jsonb ||
      !Array.isArray(contact.email_jsonb) ||
      !contact.email_jsonb.length
    )
      return "";
    const work = contact.email_jsonb.find(
      (e) => e && typeof e === "object" && e.type === "Work",
    );
    if (work && work.email) return work.email;
    const first = contact.email_jsonb.find(
      (e) => e && typeof e === "object" && e.email,
    );
    return first ? first.email || "" : "";
  };

  const getPrimaryPhone = (contact: Contact) => {
    if (
      !contact.phone_jsonb ||
      !Array.isArray(contact.phone_jsonb) ||
      !contact.phone_jsonb.length
    )
      return "";
    const work = contact.phone_jsonb.find(
      (p) => p && typeof p === "object" && p.type === "Work",
    );
    if (work && work.number) return work.number;
    const first = contact.phone_jsonb.find(
      (p) => p && typeof p === "object" && p.number,
    );
    return first ? first.number || "" : "";
  };

  const renderStrengthValue = (value: string | undefined) => {
    if (value === "A") {
      return (
        <span className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 font-semibold px-2 py-0.5 rounded text-[10px] whitespace-nowrap">
          A - Strong
        </span>
      );
    }
    if (value === "B") {
      return (
        <span className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 font-semibold px-2 py-0.5 rounded text-[10px] whitespace-nowrap">
          B - Medium
        </span>
      );
    }
    if (value === "C") {
      return (
        <span className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 font-semibold px-2 py-0.5 rounded text-[10px] whitespace-nowrap">
          C - Weak
        </span>
      );
    }
    return <span className="text-muted-foreground text-xs">-- None --</span>;
  };

  const relationshipTypeChoices = [
    { id: "Customer", name: "Customer" },
    { id: "Ecosystem", name: "Ecosystem" },
    { id: "Partner", name: "Partner" },
    { id: "Prospect", name: "Prospect" },
    { id: "Referral Source", name: "Referral Source" },
    { id: "Student", name: "Student" },
    { id: "Vendor", name: "Vendor" },
    { id: "Other", name: "Other" },
  ];

  const influenceChoices = [
    { id: "1", name: "1 - Decision Maker" },
    { id: "2", name: "2 - Influencer" },
    { id: "3", name: "3 - Neither" },
  ];

  const nextRelationshipMoveChoices = [
    { id: "Comment", name: "Comment" },
    { id: "Connect", name: "Connect" },
    { id: "DM", name: "DM" },
    { id: "Email", name: "Email" },
    { id: "Useful Resource", name: "Useful Resource" },
    { id: "Intro Ask", name: "Intro Ask" },
    { id: "Referral Ask", name: "Referral Ask" },
    { id: "Podcast Invite", name: "Podcast Invite" },
    { id: "Meeting Prep", name: "Meeting Prep" },
    { id: "Wait", name: "Wait" },
    { id: "Disqualify", name: "Disqualify" },
  ];

  return (
    <div className="w-full overflow-x-auto rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12 px-4 py-3">
              <Checkbox
                checked={allSelected}
                onCheckedChange={handleToggleSelectAll}
                className="cursor-pointer"
              />
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground py-3 min-w-[200px]">
              Name
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground py-3 min-w-[150px]">
              Company
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground py-3 min-w-[180px]">
              Location
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground py-3 min-w-[180px]">
              Email
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground py-3 min-w-[130px]">
              Phone
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground py-3 min-w-[160px]">
              Pipeline Stage
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground py-3 min-w-[140px]">
              Type
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground py-3 min-w-[120px]">
              Strength
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground py-3 min-w-[110px]">
              Influence
            </TableHead>
            <TableHead className="font-semibold text-xs text-muted-foreground py-3 min-w-[140px]">
              Next Move
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contacts.map((contact) => {
            const currentCompany = companies.find(
              (c) => c.id === contact.company_id,
            );
            const currentStage = (connectionPipelineStages ?? []).find(
              (s) => s.value === contact.connection_pipeline_stage,
            );
            const currentType = relationshipTypeChoices.find(
              (t) => t.id === contact.relationship_motion,
            );
            const currentInfluence = influenceChoices.find(
              (i) => i.id === contact.influence,
            );
            const currentMove = nextRelationshipMoveChoices.find(
              (m) => m.id === contact.next_relationship_move,
            );

            return (
              <RecordContextProvider key={contact.id} value={contact}>
                <TableRow
                  className={`hover:bg-muted/50 transition-colors ${
                    selectedIds.includes(contact.id) ? "bg-muted/30" : ""
                  }`}
                >
                  {/* Selection Checkbox */}
                  <TableCell className="px-4 py-2">
                    <Checkbox
                      checked={selectedIds.includes(contact.id)}
                      onCheckedChange={() =>
                        handleToggleItem(contact.id, {
                          shiftKey: false,
                        } as any)
                      }
                      className="cursor-pointer"
                    />
                  </TableCell>

                  {/* Name (Avatar + Link) */}
                  <TableCell className="py-2">
                    <Link
                      to={`/contacts/${contact.id}/show`}
                      className="flex items-center gap-3 hover:underline group"
                    >
                      <Avatar />
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium text-sm text-foreground group-hover:text-primary transition-colors truncate">
                          {contact.first_name} {contact.last_name || ""}
                        </span>
                        {contact.title && (
                          <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {contact.title}
                          </span>
                        )}
                      </div>
                    </Link>
                  </TableCell>

                  {/* Company inline dropdown */}
                  <TableCell className="py-1">
                    <Select
                      value={
                        contact.company_id
                          ? String(contact.company_id)
                          : "__none__"
                      }
                      onValueChange={(val) =>
                        handleFieldChange(
                          contact,
                          "company_id",
                          val === "__none__" ? null : val,
                        )
                      }
                    >
                      <SelectTrigger className="border-none shadow-none bg-transparent hover:bg-muted/60 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-2 py-1 h-8 text-xs w-full justify-between font-normal cursor-pointer rounded">
                        <span className="truncate max-w-[130px]">
                          {currentCompany ? (
                            currentCompany.name
                          ) : (
                            <span className="text-muted-foreground/50">
                              -- None --
                            </span>
                          )}
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- None --</SelectItem>
                        {companies.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>

                  {/* Location */}
                  <TableCell
                    className="text-xs text-muted-foreground py-2 truncate max-w-[180px]"
                    title={[
                      contact.location_source
                        ? `Source: ${contact.location_source}`
                        : null,
                      contact.location_confidence
                        ? `Confidence: ${contact.location_confidence}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  >
                    {[contact.city, contact.region, contact.country]
                      .filter(Boolean)
                      .join(", ") || (
                      <span className="text-muted-foreground/30">--</span>
                    )}
                  </TableCell>

                  {/* Email */}
                  <TableCell
                    className="text-xs text-muted-foreground py-2 truncate max-w-[180px]"
                    title={getPrimaryEmail(contact)}
                  >
                    {getPrimaryEmail(contact) || (
                      <span className="text-muted-foreground/30">--</span>
                    )}
                  </TableCell>

                  {/* Phone */}
                  <TableCell
                    className="text-xs text-muted-foreground py-2 truncate max-w-[130px]"
                    title={getPrimaryPhone(contact)}
                  >
                    {getPrimaryPhone(contact) || (
                      <span className="text-muted-foreground/30">--</span>
                    )}
                  </TableCell>

                  {/* Pipeline Stage inline dropdown */}
                  <TableCell className="py-1">
                    <Select
                      value={contact.connection_pipeline_stage || "__none__"}
                      onValueChange={(val) =>
                        handleFieldChange(
                          contact,
                          "connection_pipeline_stage",
                          val === "__none__" ? null : val,
                        )
                      }
                    >
                      <SelectTrigger className="border-none shadow-none bg-transparent hover:bg-muted/60 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-2 py-1 h-8 text-xs w-full justify-between font-normal cursor-pointer rounded">
                        <span className="truncate max-w-[140px] text-left">
                          {currentStage ? (
                            currentStage.label
                          ) : (
                            <span className="text-muted-foreground/50">
                              -- Select Stage --
                            </span>
                          )}
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- None --</SelectItem>
                        {(connectionPipelineStages ?? []).map((stage) => (
                          <SelectItem key={stage.value} value={stage.value}>
                            {stage.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>

                  {/* Relationship Type inline dropdown */}
                  <TableCell className="py-1">
                    <Select
                      value={contact.relationship_motion || "__none__"}
                      onValueChange={(val) =>
                        handleFieldChange(
                          contact,
                          "relationship_motion",
                          val === "__none__" ? null : val,
                        )
                      }
                    >
                      <SelectTrigger className="border-none shadow-none bg-transparent hover:bg-muted/60 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-2 py-1 h-8 text-xs w-full justify-between font-normal cursor-pointer rounded">
                        <span className="truncate">
                          {currentType ? (
                            currentType.name
                          ) : (
                            <span className="text-muted-foreground/50">
                              -- Select Type --
                            </span>
                          )}
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- None --</SelectItem>
                        {relationshipTypeChoices.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>

                  {/* Strength inline dropdown */}
                  <TableCell className="py-1">
                    <Select
                      value={contact.status || "__none__"}
                      onValueChange={(val) =>
                        handleFieldChange(
                          contact,
                          "status",
                          val === "__none__" ? null : val,
                        )
                      }
                    >
                      <SelectTrigger className="border-none shadow-none bg-transparent hover:bg-muted/60 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-2 py-1 h-8 text-xs w-full justify-between font-normal cursor-pointer rounded">
                        <div className="flex items-center">
                          {renderStrengthValue(contact.status)}
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- None --</SelectItem>
                        <SelectItem value="A">
                          A - Strong (Pick up phone)
                        </SelectItem>
                        <SelectItem value="B">
                          B - Medium (Recognize name)
                        </SelectItem>
                        <SelectItem value="C">
                          C - Weak (Peripheral/Unknown)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>

                  {/* Influence inline dropdown */}
                  <TableCell className="py-1">
                    <Select
                      value={contact.influence || "__none__"}
                      onValueChange={(val) =>
                        handleFieldChange(
                          contact,
                          "influence",
                          val === "__none__" ? null : val,
                        )
                      }
                    >
                      <SelectTrigger className="border-none shadow-none bg-transparent hover:bg-muted/60 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-2 py-1 h-8 text-xs w-full justify-between font-normal cursor-pointer rounded">
                        <span className="truncate">
                          {currentInfluence ? (
                            currentInfluence.name.split(" - ")[0]
                          ) : (
                            <span className="text-muted-foreground/50">
                              -- Select --
                            </span>
                          )}
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- None --</SelectItem>
                        {influenceChoices.map((influence) => (
                          <SelectItem key={influence.id} value={influence.id}>
                            {influence.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>

                  {/* Next Move inline dropdown */}
                  <TableCell className="py-1">
                    <Select
                      value={contact.next_relationship_move || "__none__"}
                      onValueChange={(val) =>
                        handleFieldChange(
                          contact,
                          "next_relationship_move",
                          val === "__none__" ? null : val,
                        )
                      }
                    >
                      <SelectTrigger className="border-none shadow-none bg-transparent hover:bg-muted/60 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-2 py-1 h-8 text-xs w-full justify-between font-normal cursor-pointer rounded">
                        <span className="truncate">
                          {currentMove ? (
                            currentMove.name
                          ) : (
                            <span className="text-muted-foreground/50">
                              -- Select Move --
                            </span>
                          )}
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- None --</SelectItem>
                        {nextRelationshipMoveChoices.map((move) => (
                          <SelectItem key={move.id} value={move.id}>
                            {move.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              </RecordContextProvider>
            );
          })}
          {contacts.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={10}
                className="text-center py-8 text-muted-foreground"
              >
                No contacts found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
